import type {
  NetworkDashboardCategoryPdm,
  NetworkDashboardCohortUnit,
  NetworkDashboardStoreAgeFilter,
} from '@gozoki/api-types'
import type { DateRangePreset } from '~/components/ui/date-range-picker'

/** Same shape as `DateRangePicker` value (`from: null` = unbounded past for « Toujours »). */
export interface NetworkDashboardDateRange {
  from: Date | null
  to: Date
}

export interface NetworkDashboardCohortSelection {
  enabled: boolean
  unit: NetworkDashboardCohortUnit
  value: number
}

export type NetworkDashboardComparableSelectionMode = 'auto' | 'custom'

/**
 * Start date sent to the API when the user picks « Toujours » (picker uses `from: null`).
 * Must be on or before any `network_daily_kpis.date` you need included.
 */
export const NETWORK_DASHBOARD_ALL_TIME_START_ISO = '2020-01-01'

/** One selectable metric (CA, UC, …) shown in the left « Indicateur » column. */
export interface NetworkFilterIndicatorOption {
  id: string
  label: string
}

export interface NetworkFiltersRowSelection {
  storeIds: number[]
  storeAgeFilter: NetworkDashboardStoreAgeFilter
  /**
   * Explicit age-selection store ids (partial custom). Omitted when the preset is exactly
   * default / all / mature / young without a CSV list.
   */
  ageStoreIds?: number[]
  dateRange: NetworkDashboardDateRange
  cohort?: NetworkDashboardCohortSelection
  /** Omitted ⇒ dialog defaults to `'7d'` for the picker highlight. */
  datePreset?: DateRangePreset | null
}

/** Dashboard row only, or first row when comparing. */
export type NetworkFiltersComparisonMode = 'none' | 'dual'

/** Result of « Appliquer » — callers branch on `comparisonMode`. */
export type NetworkFiltersApplyPayload =
  | {
      comparisonMode: 'none'
      indicatorId?: string
      categoryIds?: number[]
      storeIds: number[]
      storeAgeFilter: NetworkDashboardStoreAgeFilter
      ageStoreIds?: number[]
      dateRange: NetworkDashboardDateRange
      datePreset?: DateRangePreset | null
      cohort?: NetworkDashboardCohortSelection
    }
  | {
      comparisonMode: 'dual'
      comparableSelectionMode: NetworkDashboardComparableSelectionMode
      principal: {
        indicatorId?: string
        categoryIds?: number[]
        storeIds: number[]
        storeAgeFilter: NetworkDashboardStoreAgeFilter
        ageStoreIds?: number[]
        dateRange: NetworkDashboardDateRange
        datePreset?: DateRangePreset | null
        cohort?: NetworkDashboardCohortSelection
      }
      comparable: {
        indicatorId?: string
        categoryIds?: number[]
        storeIds: number[]
        storeAgeFilter: NetworkDashboardStoreAgeFilter
        ageStoreIds?: number[]
        dateRange: NetworkDashboardDateRange
        datePreset?: DateRangePreset | null
        cohort?: NetworkDashboardCohortSelection
      }
    }

export interface NetworkFilterIndicatorColumnConfig {
  options: NetworkFilterIndicatorOption[]
  defaultSelectedId?: string
}

export interface NetworkFilterCategoryColumnConfig {
  options: NetworkDashboardCategoryPdm[]
  defaultSelectedIds?: number[]
}
